import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';

/**
 * Per-table column layout — order, hidden set, and the two frozen edges —
 * stored AGAINST THE ACCOUNT.
 *
 * ## Where it lives, and why not the two obvious alternatives
 *
 * **`preferences.columns.<scope>`, a SIBLING of `preferences.tables`.** The
 * owner's 2026-08-30 ruling names the things that move off `localStorage` and
 * onto the account, and **`columns` is one of the six by name**. The reason in
 * that ruling applies here more than to anything else on the list: browser
 * storage is per browser and per device, so one person configures the same table
 * twice, and clearing site data throws away the most expensive thing on the list
 * to rebuild by hand.
 *
 * ⛔ **NOT `localStorage`, not even as a cache.** Where a preference is
 * server-side the browser copy is at best a second authority, and this repo's
 * standing rule is to pick one and say so at the call site — otherwise somebody
 * signing in on a second device gets whichever loaded last. There is no local
 * copy here at all.
 *
 * ⛔ **NOT THE URL.** Saved FILTERS go into the query string because they
 * describe WHICH ROWS, and a shared link must show the recipient what the sender
 * saw. Column layout describes the reader's own chrome and has no joint meaning
 * with a filter — putting it in a link would impose the sender's hidden columns
 * on the recipient. `useSavedFilters` already draws exactly this line with
 * `independentKeys`, and its value type is `Record<string, string>`, which an
 * ordered list plus three sets cannot honestly be squeezed into.
 *
 * ⛔ **NOT nested inside `preferences.tables`.** That branch is the saved-filter
 * scopes, and its whole-set replace logic treats every key it finds as one more
 * filter to delete. A layout parked in there would be wiped by the next filter
 * save. `landingPage` is a sibling for the same reason.
 *
 * ## The two rules inherited from the kanban column control, verbatim
 *
 * 🔴 **THE HIDDEN IDS ARE STORED, NEVER THE VISIBLE ONES.** Storing the visible
 * set makes every column that appears LATER default to hidden — it silently
 * disappears for everybody who ever touched the control, with no way to discover
 * it short of clearing storage. Storing the hidden set inverts that: anything a
 * table gains is visible, and **a stored preference can only ever subtract**.
 * That exact bug reached this repo once through a persisted sidebar accordion
 * and made the catalog pages look broken.
 *
 * 🔴 **A TABLE IS NEVER LEFT WITH NOTHING IN IT.** Hiding the last visible
 * column is REFUSED, not obeyed — and the caller disables that one switch so the
 * refusal is visible before the click rather than felt after it. Quietly calling
 * "show all" instead was rejected on the kanban board for a reason that holds
 * here: it answers a click on one column by changing several others, which reads
 * as a bug even when it is kind.
 */

/** The four-part layout of one table, as stored. */
export interface ColumnLayout {
  /** Explicit order. Keys absent from it keep their declared position. */
  order: readonly string[];
  /** HIDDEN keys — never the visible ones. See the rule above. */
  hidden: readonly string[];
  freezeLeft: readonly string[];
  freezeRight: readonly string[];
}

export const EMPTY_LAYOUT: ColumnLayout = Object.freeze({
  order: Object.freeze([]) as readonly string[],
  hidden: Object.freeze([]) as readonly string[],
  freezeLeft: Object.freeze([]) as readonly string[],
  freezeRight: Object.freeze([]) as readonly string[],
});

const COLUMNS_KEY = 'columns';
const PARTS = ['order', 'hidden', 'freezeLeft', 'freezeRight'] as const;

/**
 * Which keys the stored branch held when we last read it.
 *
 * ⚠️ **THE SAME BASELINE HAZARD `preferences-store` DOCUMENTS, AND FOR THE SAME
 * REASON.** The API merges an object over an object RECURSIVELY, so a patch
 * carrying three of the four parts leaves the fourth behind. Every part in the
 * baseline that is absent from the new layout is therefore sent as an explicit
 * `null`, which deletes. The baseline is maintained from the RESPONSE, never
 * from what was sent.
 *
 * ⚠️ Module-level and keyed by scope, NOT by user — the API has no concept of
 * another person's preferences. `resetColumnLayoutBaselines()` must be called
 * wherever `resetPreferenceBaselines()` is, at all three identity changes
 * (sign-out, support-session start, support-session end). A stale baseline would
 * send `null` for the OPERATOR's parts against the TARGET's document.
 */
const baselines = new Map<string, readonly string[]>();

export function resetColumnLayoutBaselines(): void {
  baselines.clear();
  inFlight = null;
  // A write queued as one identity must never be sent as the next.
  writes.clear();
  writeEpoch += 1;
  writeHandoff({});
}

/**
 * The write queue, one per scope — 2026-10-04, after the owner reported the
 * companies column manager "not working on refresh".
 *
 * 🔴 **THREE WAYS A CHANGE WAS LOST, EACH SILENT, ALL CLOSED HERE.**
 *
 *   1. **A refresh cancelled the save.** The `PATCH` was an ordinary `fetch`,
 *      which the browser aborts on reload — so hide a column, press F5, and the
 *      column came back. Every write now goes out with `keepalive`.
 *   2. **Two quick changes could arrive out of order.** Each write carries the
 *      WHOLE part arrays, so the last to ARRIVE wins, and HTTP does not promise
 *      that is the last one sent. Writes now run ONE AT A TIME per scope, and
 *      changes made while one is in the air coalesce into the next.
 *   3. **Leaving and coming back read a document the save had not reached yet.**
 *      A remount's read could land before the write committed and put the old
 *      layout back on screen — and the next change would be built on it. While a
 *      scope has an unconfirmed write, or changed since the read began, the read
 *      answer is ignored in favour of what this session last set.
 *
 * Also flushed on `pagehide`: a change queued behind an in-flight write is sent
 * straight away with `keepalive` rather than waiting for a page that is going.
 *
 * ⚠️ A failed write is retried twice, then left: the layout stays applied for
 * this session (`dirty` keeps the read from overruling it) and the next change
 * tries again. It is never rolled back under the reader's hands.
 */
interface ScopeWrites {
  /** What this session last set — the authority while `dirty`. */
  latest: ColumnLayout;
  /** Bumped on every change, so a read can tell it was overtaken. */
  gen: number;
  /** True until a write of `latest` is confirmed by the server. */
  dirty: boolean;
  /** The next layout to send, or null when nothing is queued. */
  pending: ColumnLayout | null;
  running: boolean;
}

const writes = new Map<string, ScopeWrites>();
let writeGeneration = 0;
let writeEpoch = 0;
let pageHideBound = false;

function patchBody(scope: string, next: ColumnLayout) {
  // Every part is empty: delete the scope branch outright rather than writing
  // four empty arrays, so "reset to default" leaves nothing behind. Otherwise
  // every part is named — an empty one as `null`, which deletes — because the
  // server merges objects recursively and an omitted part would survive.
  const patch: Record<string, readonly string[] | null> | null =
    presentParts(next).length === 0
      ? null
      : Object.fromEntries(PARTS.map((part) => [part, next[part].length > 0 ? next[part] : null]));
  return { preferences: { [COLUMNS_KEY]: { [scope]: patch } } };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function drain(scope: string, state: ScopeWrites, epoch: number): Promise<void> {
  if (state.running) return;
  state.running = true;
  let attempt = 0;
  try {
    while (state.pending !== null && epoch === writeEpoch) {
      const layout = state.pending;
      state.pending = null;
      try {
        const response = await api.updatePreferences(patchBody(scope, layout), undefined, true);
        if (epoch !== writeEpoch) return;
        baselines.set(scope, presentParts(readLayout(branchOf(response.data.preferences, scope))));
        attempt = 0;
        if (state.pending === null && state.latest === layout) state.dirty = false;
      } catch {
        if (epoch !== writeEpoch) return;
        // A newer change is already queued and supersedes this one; otherwise
        // retry the same layout, twice, with a short pause.
        if (state.pending === null && attempt < 2) {
          attempt += 1;
          state.pending = layout;
          await delay(1500 * attempt);
        }
      }
    }
  } finally {
    state.running = false;
  }
}

/*
 * 🔴 **THE RELOAD HAND-OFF — the one gap `keepalive` cannot close.** The save
 * survives the reload, but the NEW page's read can reach the server before that
 * save commits, and then shows the old layout. So every layout still unconfirmed
 * at `pagehide` is parked in `sessionStorage` for a few seconds, and the next
 * read of that scope prefers it and re-sends it.
 *
 * ⚠️ **This is NOT a second store.** It holds only writes the server has not
 * confirmed, it is consumed by the first read that finds it, and it expires in
 * `HANDOFF_TTL_MS` — after that the server's document is the only authority
 * again. `sessionStorage` is per tab, so it cannot leak to another device or
 * another tab, and an identity change clears it.
 */
const HANDOFF_KEY = 'emerge.columnLayout.unsaved';
const HANDOFF_TTL_MS = 15_000;

function readHandoff(): Record<string, { layout: ColumnLayout; at: number }> {
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed !== null && typeof parsed === 'object'
      ? (parsed as Record<string, { layout: ColumnLayout; at: number }>)
      : {};
  } catch {
    return {};
  }
}

function writeHandoff(entries: Record<string, { layout: ColumnLayout; at: number }>): void {
  try {
    if (Object.keys(entries).length === 0) sessionStorage.removeItem(HANDOFF_KEY);
    else sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(entries));
  } catch {
    // Storage blocked: the keepalive write still goes; only the hand-off is lost.
  }
}

/** Take (and remove) this scope's parked layout, if it is still fresh. */
function takeHandoff(scope: string): ColumnLayout | null {
  const entries = readHandoff();
  const entry = entries[scope];
  if (entry === undefined) return null;
  delete entries[scope];
  writeHandoff(entries);
  if (typeof entry.at !== 'number' || Date.now() - entry.at > HANDOFF_TTL_MS) return null;
  return readLayout(entry.layout);
}

function flushOnPageHide(): void {
  const parked = readHandoff();
  for (const [scope, state] of writes) {
    if (state.dirty) parked[scope] = { layout: state.latest, at: Date.now() };
    if (state.pending === null) continue;
    const layout = state.pending;
    state.pending = null;
    void api.updatePreferences(patchBody(scope, layout), undefined, true).catch(() => {});
  }
  writeHandoff(parked);
}

function enqueueWrite(scope: string, layout: ColumnLayout): void {
  if (!pageHideBound && typeof window !== 'undefined') {
    window.addEventListener('pagehide', flushOnPageHide);
    pageHideBound = true;
  }
  writeGeneration += 1;
  const state = writes.get(scope) ?? {
    latest: layout,
    gen: 0,
    dirty: true,
    pending: null,
    running: false,
  };
  state.latest = layout;
  state.gen = writeGeneration;
  state.dirty = true;
  state.pending = layout;
  writes.set(scope, state);
  void drain(scope, state, writeEpoch);
}

/**
 * The read every controller shares while one is already in the air.
 *
 * 🔴 **ONE PAGE MOUNTS FIVE CONTROLLERS AND WAS MAKING FIVE IDENTICAL
 * REQUESTS.** `/university-partnerships` renders five cards through one
 * component, each with its own column scope — but `preferences` is ONE
 * document, so five readers were asking for the same body five times on a
 * single page load. ⚠️ *SUPERSEDED: "That page's own comments already reject a
 * feature for costing ~150 requests, and the API's limit is 120/60s **shared
 * across the whole office**, so this is a real budget rather than a tidiness
 * point."* The limit is **12,000/60s** (corrected 2026-09-17), so on that
 * arithmetic five duplicate reads really are the tidiness point that sentence
 * denied.
 *
 * 🔴 **WHAT IT RESTS ON NOW IS THE SHAPE, AND THIS FILE HAS ALREADY PAID FOR IT.**
 * N requests where one would do is the habit that made `showAll`'s loop a silent
 * race — each `PATCH` carried the WHOLE array and the last to ARRIVE won, leaving
 * the screen right and the STORED layout wrong (see its note on
 * `ColumnLayoutController`). A read cannot lose that race, so deduping is the
 * cheap half of the same mistake; the limit is still SHARED per IP, and this repo
 * has already watched a burst of refusals read as a dead session.
 *
 * ⚠️ **CONCURRENT DEDUPE, NOT A CACHE.** The promise is dropped the moment it
 * settles, so a later mount reads the document again. Holding it would mean a
 * layout saved on one page is stale on the next — and worse, a support-session
 * switch would serve the operator's document to the target. Sharing only what
 * is already in the air has neither failure mode.
 *
 * Cleared by `resetColumnLayoutBaselines()` too: an in-flight read begun as one
 * identity must not be handed to the next.
 */
let inFlight: Promise<unknown> | null = null;

function readPreferences(signal: AbortSignal): Promise<unknown> {
  if (inFlight !== null) return inFlight;
  /*
   * ⚠️ **The shared read deliberately carries NO signal.** Aborting it would
   * cancel the request for every other controller waiting on it, so the first
   * card to unmount would take the other four down with it. Each caller still
   * checks its own `signal.aborted` before using the answer, which is what that
   * check is for.
   */
  const request = api
    .getPreferences()
    .then((response) => response.data.preferences as unknown)
    .finally(() => {
      inFlight = null;
    });
  inFlight = request;
  void signal;
  return request;
}

function readStringArray(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
}

/** The stored branch, read defensively — the document is unvalidated by design. */
function readLayout(branch: unknown): ColumnLayout {
  if (branch === null || typeof branch !== 'object') return EMPTY_LAYOUT;
  const record = branch as Record<string, unknown>;
  return {
    order: readStringArray(record.order),
    hidden: readStringArray(record.hidden),
    freezeLeft: readStringArray(record.freezeLeft),
    freezeRight: readStringArray(record.freezeRight),
  };
}

function branchOf(preferences: unknown, scope: string): unknown {
  if (preferences === null || typeof preferences !== 'object') return null;
  const columns = (preferences as Record<string, unknown>)[COLUMNS_KEY];
  if (columns === null || typeof columns !== 'object') return null;
  return (columns as Record<string, unknown>)[scope] ?? null;
}

function presentParts(layout: ColumnLayout): readonly string[] {
  return PARTS.filter((part) => layout[part].length > 0);
}

/**
 * Order the live keys by the stored layout.
 *
 * ⚠️ **THE LIVE KEY LIST IS THE AUTHORITY, NOT THE STORED ORDER.** A key the
 * table no longer renders is dropped, and a key the table has GAINED lands in
 * its declared position rather than at the end — a new column appearing last on
 * everybody's table who ever reordered one is the same silent-subtraction bug as
 * storing the visible set.
 */
export function applyOrder(
  keys: readonly string[],
  order: readonly string[],
): readonly string[] {
  if (order.length === 0) return keys;
  const live = new Set(keys);
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const key of order) {
    if (live.has(key) && !seen.has(key)) {
      ordered.push(key);
      seen.add(key);
    }
  }
  // Anything the stored order never mentioned keeps its declared position,
  // spliced in at the index the table declares it at.
  keys.forEach((key, index) => {
    if (!seen.has(key)) ordered.splice(Math.min(index, ordered.length), 0, key);
  });
  return ordered;
}

/**
 * Put the code-pinned LEFT keys first, in the order the caller declared them,
 * keeping everything else in its layout order. Only LIVE keys are placed: a
 * pinned-left key the table does not render is ignored rather than invented.
 */
function pinLeftFirst(
  ordered: readonly string[],
  pinnedLeft: readonly string[],
): readonly string[] {
  if (pinnedLeft.length === 0) return ordered;
  const live = new Set(ordered);
  const head = pinnedLeft.filter((key, index) => live.has(key) && pinnedLeft.indexOf(key) === index);
  if (head.length === 0) return ordered;
  const headSet = new Set(head);
  return [...head, ...ordered.filter((key) => !headSet.has(key))];
}

export interface ColumnLayoutOptions {
  /**
   * Keys the CODE pins to the LEFT edge — always first, always visible, always
   * frozen left at offset 0, never movable, hideable or re-freezable.
   *
   * ⚠️ **NOT `pinnedKeys`, which means the RIGHT edge** (`sticky right-0`, the
   * actions lane). These are rendered with `frozen="left"`, because
   * `frozenSideOf` answers `'left'` for them — so `useFrozenOffsets` needs no
   * extra argument: they lead `visibleKeys`, so they take offset 0 and every
   * user-frozen-left column's offset shifts by their width.
   *
   * ⛔ **Never pass these in `useFrozenOffsets`' `pinnedKeys`** — that list
   * means RIGHT and would stack them on the actions lane.
   *
   * A stored layout cannot override any of this: a stored hidden entry, order
   * or freeze for one of these keys is ignored, not obeyed.
   */
  pinnedLeftKeys?: readonly string[];
  /**
   * A retired column key → the live keys that replaced it — 2026-10-25, when the
   * single Companies `stageStatus` column became one `stageStatus:<stageId>`
   * column per stage.
   *
   * 🔴 **WITHOUT THIS, A COLUMN SOMEBODY HID COMES BACK AS SEVERAL.** The stored
   * set names the OLD key, which no table renders any more, so every replacement
   * would arrive visible — the hidden-ids rule working exactly as designed, and
   * still the wrong answer for the person who had hidden that column.
   *
   * While a stored `hidden` names an alias, every target the stored layout does
   * not otherwise MENTION (in `order`, `hidden` or either freeze list) is treated
   * as hidden. A mentioned target already has an explicit saved state, and that
   * wins. The first real change of any kind rewrites the alias as the explicit
   * target ids and drops it — after that the alias is no longer needed.
   *
   * ⚠️ **An alias whose target list is empty or absent is LEFT ALONE**, never
   * dropped. Targets usually come from masters that load after the first render;
   * a change made in that moment must not throw the alias away and with it the
   * person's hidden column. Omitting the option changes nothing.
   */
  legacyHiddenAliases?: Readonly<Record<string, readonly string[]>>;
}

/**
 * The layout with every `legacyHiddenAliases` entry it holds expanded into
 * explicit hidden ids. Returns its input (same identity) when there is nothing
 * to expand, so a caller can treat identity as "unchanged".
 *
 * An alias key that is itself a live key is never treated as an alias: a table
 * that renders it means that column.
 */
export function expandLegacyHiddenAliases(
  layout: ColumnLayout,
  aliases: Readonly<Record<string, readonly string[]>> | undefined,
  liveKeys: readonly string[],
): ColumnLayout {
  if (aliases === undefined || layout.hidden.length === 0) return layout;
  const live = new Set(liveKeys);
  const isAlias = (key: string) =>
    !live.has(key) && Object.prototype.hasOwnProperty.call(aliases, key) && (aliases[key]?.length ?? 0) > 0;
  if (!layout.hidden.some(isAlias)) return layout;
  const mentioned = new Set<string>();
  for (const part of PARTS) for (const key of layout[part]) mentioned.add(key);
  const hidden: string[] = [];
  const seen = new Set<string>();
  const add = (key: string) => {
    if (seen.has(key)) return;
    seen.add(key);
    hidden.push(key);
  };
  for (const key of layout.hidden) {
    if (!isAlias(key)) {
      add(key);
      continue;
    }
    for (const target of aliases[key] ?? []) {
      if (!mentioned.has(target)) add(target);
    }
  }
  return { ...layout, hidden };
}

/**
 * The new stored order for a move: the live order as moved, with every key the
 * PREVIOUS stored order named but this table does not render right now kept in
 * place after the live key it followed.
 *
 * ⚠️ **Dropping them is how a column that comes back loses its saved place.**
 * Columns that come from masters (per-stage status columns) can be absent for a
 * moment or for a while; a move made meanwhile must not rewrite their history.
 * With no such keys the result is exactly the live order, as before.
 */
function keepAbsentOrder(
  liveOrder: readonly string[],
  previous: readonly string[],
  liveKeys: readonly string[],
): readonly string[] {
  const live = new Set(liveKeys);
  const after = new Map<string | null, string[]>();
  let anchor: string | null = null;
  for (const key of previous) {
    if (live.has(key)) {
      anchor = key;
      continue;
    }
    const group = after.get(anchor) ?? [];
    group.push(key);
    after.set(anchor, group);
  }
  if (after.size === 0) return liveOrder;
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (key: string) => {
    if (seen.has(key)) return;
    seen.add(key);
    out.push(key);
  };
  (after.get(null) ?? []).forEach(push);
  for (const key of liveOrder) {
    push(key);
    (after.get(key) ?? []).forEach(push);
  }
  return out;
}

export interface ColumnLayoutController {
  /** The live keys in layout order, hidden ones removed. */
  visibleKeys: readonly string[];
  /** Every live key in layout order, hidden ones INCLUDED — the manager's list. */
  orderedKeys: readonly string[];
  isVisible: (key: string) => boolean;
  frozenSideOf: (key: string) => 'left' | 'right' | undefined;
  toggleVisible: (key: string) => void;
  /**
   * Unhide every live, unlocked column — ONE state change and ONE write.
   *
   * 🔴 **THIS EXISTS BECAUSE THE LOOP OVER `toggleVisible` WAS A SILENT RACE, NOT
   * BECAUSE IT WAS UNTIDY.** `persist` runs from inside the `setLayout` updater,
   * so N hidden columns were N `PATCH /preferences` requests fired together —
   * and each carries the WHOLE `hidden` array, so they do not merge. The last to
   * ARRIVE wins, and HTTP promises nothing about that being the last one sent.
   * On screen the loop was exact (the updates are functional), which is exactly
   * why nothing looked wrong: a lost race left every column showing and the
   * STORED layout still hiding some, surfacing on the next page load as columns
   * quietly missing, with nothing having errored and nothing connecting it to a
   * button pressed yesterday.
   *
   * ⛔ **STAGGERING THE CALLS WAS REFUSED AND IS THE TEMPTING WRONG FIX** — it
   * narrows the window without closing it, and a rarer silent race is harder to
   * diagnose than a common one.
   *
   * ⚠️ **LOCKED KEYS STAY HIDDEN, which is not an edge case.** `toggleVisible`
   * returns early on a locked key, so the loop this replaces never unhid one
   * either; a column somebody hid yesterday and the page locked today arrives
   * hidden and immovable. Matching that exactly is what makes this a change of
   * TRANSPORT and not of behaviour.
   *
   * ⚠️ **Keys the stored set names but this table no longer renders are LEFT
   * ALONE.** Only live keys are unhidden, for the same reason `toggleVisible`
   * counts over `keys` rather than over `current.hidden`: the stored set may
   * still name ghosts, and silently dropping them here would make a table's
   * mount rewrite a scope's history.
   */
  showAll: () => void;
  /** Passing the side the key already holds clears it. */
  setFrozen: (key: string, side: 'left' | 'right' | undefined) => void;
  move: (key: string, direction: -1 | 1) => void;
  moveTo: (key: string, index: number) => void;
  reset: () => void;
  hiddenCount: number;
  isDefault: boolean;
  /** True while the first read is in flight; the table renders its default meanwhile. */
  loading: boolean;
  /**
   * The live code-pinned LEFT keys (`options.pinnedLeftKeys`), in order. Empty
   * unless the caller declared some. A column manager treats these as locked
   * AND as having no freeze controls.
   */
  pinnedLeftKeys: readonly string[];
}

/**
 * @param scope  the page's own scope string, reusing the saved-filter scopes.
 * @param keys   every column key the table declares, in its DECLARED order.
 * @param lockedKeys keys that may never be hidden or moved — the actions lane,
 *   and any column hosting the only editor for a field. **A locked key is still
 *   listed in the manager**, disabled, so its absence does not read as the
 *   feature having lost it.
 */
export function useColumnLayout(
  scope: string,
  keys: readonly string[],
  lockedKeys: readonly string[] = [],
  /**
   * Keys the page renders with `pinned` — the actions lane, almost always.
   *
   * ⚠️ **SEPARATE FROM `lockedKeys`, and the distinction is load-bearing.**
   * Locked is about hiding and reordering; pinned is about which edge a cell
   * already occupies. Conflating them made the identifying column — locked
   * everywhere, and the single best freeze-left candidate — unfreezable.
   */
  pinnedKeys: readonly string[] = [],
  /** See `ColumnLayoutOptions`. Omitting it changes nothing for existing callers. */
  options: ColumnLayoutOptions = {},
): ColumnLayoutController {
  const [layout, setLayout] = useState<ColumnLayout>(EMPTY_LAYOUT);
  const [loading, setLoading] = useState(true);
  const locked = useMemo(() => new Set(lockedKeys), [lockedKeys]);
  const pinned = useMemo(() => new Set(pinnedKeys), [pinnedKeys]);

  // `keys` is a fresh array on most renders; the signature is what the effects
  // may depend on without looping.
  const keysSignature = JSON.stringify(keys);

  // Same reasoning: `options` defaults to a fresh `{}` on every render.
  const pinnedLeftSignature = JSON.stringify(options.pinnedLeftKeys ?? []);
  const pinnedLeftDeclared = useMemo(
    () => JSON.parse(pinnedLeftSignature) as readonly string[],
    [pinnedLeftSignature],
  );
  const pinnedLeft = useMemo(() => new Set(pinnedLeftDeclared), [pinnedLeftDeclared]);

  // Same again: the alias map is usually built inline from masters.
  const aliasSignature = JSON.stringify(options.legacyHiddenAliases ?? null);
  const aliases = useMemo(() => {
    const parsed = JSON.parse(aliasSignature) as Readonly<Record<string, readonly string[]>> | null;
    return parsed === null ? undefined : parsed;
  }, [aliasSignature]);
  // A stable copy of `keys` for the callbacks below, so adding the alias
  // expansion does not give `update` (and so `reset`) a new identity per render.
  const liveKeys = useMemo(() => JSON.parse(keysSignature) as readonly string[], [keysSignature]);

  /*
   * ⚠️ **No change is accepted until the stored layout has been read.** A change
   * made against the default while the read is in the air was overwritten on
   * screen the moment the read landed — and had already been SAVED, so the
   * stored document and the screen then disagreed. The manager's trigger shows
   * as loading for that moment; this ref is the guarantee behind it.
   */
  const loaded = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    loaded.current = false;
    setLoading(true);
    const startGen = writes.get(scope)?.gen ?? 0;
    readPreferences(signal)
      .then((preferences) => {
        if (signal.aborted) return;
        const branch = branchOf(preferences, scope);
        const stored = readLayout(branch);
        baselines.set(scope, presentParts(stored));
        // An unconfirmed or newer local change outranks a read that may have
        // been answered before that change reached the server.
        const local = writes.get(scope);
        const useLocal = local !== undefined && (local.dirty || local.gen !== startGen);
        const parked = useLocal ? null : takeHandoff(scope);
        if (parked !== null) {
          // Re-sent: the keepalive write probably landed, but nothing here
          // can know that, and the same layout twice is harmless.
          enqueueWrite(scope, parked);
          setLayout(parked);
        } else {
          setLayout(useLocal ? local.latest : stored);
        }
        loaded.current = true;
        setLoading(false);
      })
      .catch(() => {
        if (signal.aborted) return;
        /*
         * ⚠️ **A FAILED READ DEGRADES TO THE DEFAULT LAYOUT AND NEVER BLOCKS THE
         * TABLE.** The rows are the page; the column arrangement is chrome. This
         * repo has already shipped a load budget that silently disabled a whole
         * feature on every cold boot, and an unreachable preferences document
         * must not be able to take a list page with it.
         */
        const local = writes.get(scope);
        setLayout(local !== undefined && local.dirty ? local.latest : EMPTY_LAYOUT);
        // A failed read still unlocks the controls: the reader may arrange the
        // default, and that arrangement is saved like any other.
        loaded.current = true;
        setLoading(false);
      });
    return () => controller.abort();
  }, [scope]);

  /*
   * Queued, one at a time, with `keepalive` — see `ScopeWrites`. A failed WRITE
   * leaves the layout applied on screen and is retried; it is deliberately not
   * rolled back under the reader's hands.
   */
  const persist = useCallback((next: ColumnLayout) => enqueueWrite(scope, next), [scope]);

  const update = useCallback(
    (change: (current: ColumnLayout) => ColumnLayout) => {
      if (!loaded.current) return;
      setLayout((current) => {
        /*
         * Every change is made against the layout with its legacy aliases
         * expanded, so what is saved names the explicit ids and the alias is
         * gone. A refusal hands back `base`, which is still "nothing to do":
         * the expansion alone is never written — only a real change carries it.
         * With no aliases `base` IS `current`, exactly as before.
         */
        const base = expandLegacyHiddenAliases(current, aliases, liveKeys);
        const next = change(base);
        if (next === base) return current;
        /*
         * ⚠️ **A CHANGE THAT RETURNS ITS INPUT WRITES NOTHING.** Every refusal in
         * this hook is spelled `return current` — hiding the last visible column,
         * toggling a locked key, a `showAll` with nothing left to show — and each
         * was firing a `PATCH` that restored the document to what it already
         * held. Harmless in effect and ⚠️ *SUPERSEDED: "not in cost: they spend
         * from a 120/60s budget shared across the whole office".* The limit is
         * **12,000/60s** (corrected 2026-09-17), so the cost is negligible — and
         * the reason survives the arithmetic unchanged: a request nobody can
         * explain is worse than one nobody made. Identity is the test because every
         * refusal already hands back the same object.
         */
        persist(next);
        return next;
      });
    },
    [persist, aliases, liveKeys],
  );

  const orderedKeys = useMemo(
    () => pinLeftFirst(applyOrder(keys, layout.order), pinnedLeftDeclared),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [keysSignature, layout.order, pinnedLeftDeclared],
  );

  const livePinnedLeft = useMemo(
    () => orderedKeys.filter((key) => pinnedLeft.has(key)),
    [orderedKeys, pinnedLeft],
  );

  // What is shown reads the EXPANDED hidden set; what is stored is untouched
  // until a real change (see `update`). Without aliases this is `layout.hidden`.
  const hiddenSet = useMemo(
    () => new Set(expandLegacyHiddenAliases(layout, aliases, liveKeys).hidden),
    [layout, aliases, liveKeys],
  );

  // A code-pinned left key is visible whatever the stored hidden set says.
  const visibleKeys = useMemo(
    () => orderedKeys.filter((key) => pinnedLeft.has(key) || !hiddenSet.has(key)),
    [orderedKeys, hiddenSet, pinnedLeft],
  );

  const frozenSideOf = useCallback(
    (key: string): 'left' | 'right' | undefined => {
      if (pinnedLeft.has(key)) return 'left';
      if (layout.freezeLeft.includes(key)) return 'left';
      if (layout.freezeRight.includes(key)) return 'right';
      return undefined;
    },
    [layout.freezeLeft, layout.freezeRight, pinnedLeft],
  );

  const toggleVisible = useCallback(
    (key: string) => {
      if (locked.has(key) || pinnedLeft.has(key)) return;
      update((current) => {
        const hidden = new Set(current.hidden);
        if (hidden.has(key)) {
          hidden.delete(key);
        } else {
          /*
           * 🔴 **REFUSED, NOT OBEYED.** Counted over the LIVE keys rather than
           * over the stored hidden set, because the stored set may still name
           * columns this table no longer has — counting those would let the last
           * real column be hidden on the strength of ghosts.
           */
          const visibleAfter = keys.filter(
            (k) => (pinnedLeft.has(k) || !hidden.has(k)) && k !== key,
          ).length;
          if (visibleAfter === 0) return current;
          hidden.add(key);
        }
        return { ...current, hidden: [...hidden] };
      });
    },
    [update, keys, locked, pinnedLeft],
  );

  /*
   * 🔴 **ONE STATE CHANGE, ONE WRITE — the whole point of the verb.** See the
   * note on `ColumnLayoutController.showAll` for what the loop it replaces cost.
   *
   * ⚠️ **The caller's own `lockedKeys` and this hook's must be the same list**,
   * and on every adopting page they are — the page hands one array to both. The
   * coupling is not new: the loop this replaces was refused key-by-key by THIS
   * set while the button's disabled state was computed from the COMPONENT's, so
   * a divergence would already have produced a button that acted on less than it
   * offered. What changes is only that the disagreement now costs one request
   * instead of several.
   */
  const showAll = useCallback(() => {
    update((current) => {
      const hidden = new Set(current.hidden);
      let removed = false;
      for (const key of keys) {
        if (locked.has(key)) continue;
        if (hidden.delete(key)) removed = true;
      }
      // Identity, not an empty patch: `update` reads it as "nothing to do".
      return removed ? { ...current, hidden: [...hidden] } : current;
    });
  }, [update, keys, locked]);

  const setFrozen = useCallback(
    (key: string, side: 'left' | 'right' | undefined) => {
      /*
       * 🔴 **PINNED, NOT LOCKED — AND GETTING THIS WRONG BROKE THE MAIN USE
       * CASE.** This guard read `locked.has(key)` for one revision, on the
       * reasoning that the locked column is the actions lane and that lane is
       * already `pinned`. That is true of the actions lane and false of
       * everything else locked: pages lock their IDENTIFYING column too —
       * Product, Company, Client — because hiding it leaves a table of figures
       * describing a record nobody can name.
       *
       * ⚠️ **The identifying column is precisely the one a reader wants frozen
       * to the left.** So the guard made freeze-left unreachable on exactly the
       * column the feature exists for, on every page that locked one. Caught by
       * the last of the first twenty-two pages to adopt it, which had locked
       * `product` as instructed and then found it could not be frozen.
       *
       * **LOCKED means cannot be hidden and cannot be reordered. It says
       * nothing about which edge a column sits at.** What genuinely cannot be
       * frozen is a column the page already renders `pinned` — that is
       * `sticky right-0`, so a stored freeze would be a second, conflicting
       * position rule the table then ignores, leaving a button that reads as
       * pressed while nothing moves.
       */
      if (pinned.has(key) || pinnedLeft.has(key)) return;
      update((current) => {
        // A key holds at most one edge, so both lists drop it before either
        // takes it — otherwise freezing right after left leaves it in both and
        // `frozenSideOf` answers with whichever is tested first.
        const freezeLeft = current.freezeLeft.filter((k) => k !== key);
        const freezeRight = current.freezeRight.filter((k) => k !== key);
        if (side === 'left') freezeLeft.push(key);
        if (side === 'right') freezeRight.push(key);
        return { ...current, freezeLeft, freezeRight };
      });
    },
    [update, pinned, pinnedLeft],
  );

  const moveTo = useCallback(
    (key: string, index: number) => {
      if (locked.has(key) || pinnedLeft.has(key)) return;
      update((current) => {
        const before = pinLeftFirst(applyOrder(keys, current.order), pinnedLeftDeclared);
        const order = before.filter((k) => k !== key);
        /*
         * Nothing may be placed ahead of a code-pinned left key, so the floor is
         * how many of them lead. With none declared the floor is 0, which is
         * exactly the previous behaviour.
         */
        const floor = order.filter((k) => pinnedLeft.has(k)).length;
        order.splice(Math.max(floor, Math.min(index, order.length)), 0, key);
        // A move the floor clamped back to where it was writes nothing.
        if (order.every((k, i) => before[i] === k)) return current;
        return { ...current, order: keepAbsentOrder(order, current.order, keys) };
      });
    },
    [update, keys, locked, pinnedLeft, pinnedLeftDeclared],
  );

  const move = useCallback(
    (key: string, direction: -1 | 1) => {
      const index = orderedKeys.indexOf(key);
      if (index === -1) return;
      moveTo(key, index + direction);
    },
    [moveTo, orderedKeys],
  );

  const reset = useCallback(() => update(() => EMPTY_LAYOUT), [update]);

  const hiddenCount = useMemo(
    () => keys.filter((key) => !pinnedLeft.has(key) && hiddenSet.has(key)).length,
    [keys, hiddenSet, pinnedLeft],
  );

  const isVisible = useCallback(
    (key: string) => pinnedLeft.has(key) || !hiddenSet.has(key),
    [hiddenSet, pinnedLeft],
  );
  const isDefault = presentParts(layout).length === 0;

  /*
   * 🔴 **THE CONTROLLER IS MEMOISED, AND THAT IS NOT A MICRO-OPTIMISATION.**
   *
   * Returned as a bare object literal this was a NEW IDENTITY ON EVERY RENDER,
   * and it pushed that cost onto every caller. Six of the first pages to adopt
   * it ended up depending on `[layout.frozenSideOf]` rather than `[layout]` —
   * correct, carefully reasoned, and flagged by `oxlint` as a missing
   * dependency in all six. Each had written the same comment explaining that
   * depending on the object itself would tear down and rebuild the offsets'
   * `ResizeObserver` on every keystroke in a search box.
   *
   * ⚠️ **Six identical workarounds is the hook's problem, not six pages'
   * problem.** And the warning is the dangerous kind: it is a false positive
   * HERE, which teaches a reader to wave the same warning away somewhere it is
   * real. A stable identity lets a caller depend on `layout` plainly, which is
   * both what the linter asks for and what is correct.
   *
   * Every member is already a `useCallback` or a `useMemo`, so this only holds
   * still what was already stable underneath.
   */
  return useMemo(
    () => ({
      visibleKeys,
      orderedKeys,
      isVisible,
      frozenSideOf,
      toggleVisible,
      showAll,
      setFrozen,
      move,
      moveTo,
      reset,
      hiddenCount,
      isDefault,
      loading,
      pinnedLeftKeys: livePinnedLeft,
    }),
    [
      livePinnedLeft,
      visibleKeys,
      orderedKeys,
      isVisible,
      frozenSideOf,
      toggleVisible,
      showAll,
      setFrozen,
      move,
      moveTo,
      reset,
      hiddenCount,
      isDefault,
      loading,
    ],
  );
}

/**
 * The px offset of each frozen column from its edge, MEASURED.
 *
 * ⚠️ **IT HAS TO BE MEASURED, AND THAT IS NOT A SHORTCUT.** Only four tables in
 * this repo use `table-fixed` with a `<colgroup>`; every other one is auto
 * layout, where a `w-*` on a `<th>` is a request the content can outvote. So the
 * rendered width of a column is knowable only from the DOM, and a frozen
 * column's offset is the sum of the rendered widths outside it.
 *
 * Pass the header row element. Offsets are recomputed on resize, because a
 * column that reflows moves every frozen column outboard of it — and a stale
 * offset does not error, it just overlaps two frozen columns by the difference.
 */
export function useFrozenOffsets(
  headRowRef: React.RefObject<HTMLTableRowElement | null>,
  visibleKeys: readonly string[],
  frozenSideOf: (key: string) => 'left' | 'right' | undefined,
  /**
   * Keys rendered with `pinned` rather than `frozen` — the actions lane on
   * almost every list page.
   *
   * 🔴 **THEY MUST BE COUNTED OR THE NEXT RIGHT-FROZEN COLUMN SITS UNDERNEATH
   * THEM.** `pinned` is `sticky right-0`, so it occupies the right edge without
   * appearing in the layout's own freeze lists. Measured without it, the first
   * right-frozen column is handed offset 0 — the same edge — and the two
   * columns stack, each fully opaque, so one simply disappears behind the
   * other. Nothing errors and the table looks like it merely lost a column.
   *
   * Found by the first page to adopt this, which had to work around it locally.
   * Baking it in means no adopter has to know.
   */
  pinnedKeys: readonly string[] = [],
): Record<string, number> {
  const [offsets, setOffsets] = useState<Record<string, number>>({});
  const pinnedSet = useMemo(() => new Set(pinnedKeys), [pinnedKeys]);
  const sideOf = useCallback(
    (key: string): 'left' | 'right' | undefined =>
      pinnedSet.has(key) ? 'right' : frozenSideOf(key),
    [pinnedSet, frozenSideOf],
  );
  const signature = `${visibleKeys.join('|')}::${visibleKeys.map(sideOf).join('|')}`;
  const lastSignature = useRef('');

  const measure = useCallback(() => {
    const row = headRowRef.current;
    if (!row) return;
    const cells = Array.from(row.children) as HTMLElement[];
    const next: Record<string, number> = {};

    /*
     * Sub-pixel widths, rounded only once at the end. `offsetWidth` rounds each
     * cell to a whole pixel, so two frozen columns could leave a 1–3px seam the
     * scrolling content showed through.
     */
    const widthOf = (index: number) => cells[index]?.getBoundingClientRect().width ?? 0;

    let left = 0;
    visibleKeys.forEach((key, index) => {
      if (sideOf(key) !== 'left') return;
      next[key] = Math.floor(left);
      left += widthOf(index);
    });

    let right = 0;
    for (let index = visibleKeys.length - 1; index >= 0; index -= 1) {
      const key = visibleKeys[index];
      if (key === undefined || sideOf(key) !== 'right') continue;
      next[key] = Math.floor(right);
      right += widthOf(index);
    }

    setOffsets((current) => {
      const same =
        Object.keys(next).length === Object.keys(current).length &&
        Object.entries(next).every(([key, value]) => current[key] === value);
      return same ? current : next;
    });
  }, [headRowRef, visibleKeys, sideOf]);

  /*
   * 🔴 **THE OBSERVED ROW CAN BE SWAPPED UNDER US, AND NOTHING SIGNALS IT.**
   *
   * Several pages render the skeleton and the loaded rows as TWO SEPARATE
   * `<Table>` mounts. When the data lands, the header row this hook measured is
   * unmounted and a different element takes its place — but the key list and the
   * frozen sides are identical, so a signature-keyed effect has no reason to
   * re-run. The result: every offset still holds the SKELETON's widths, which
   * under `table-layout: auto` are not the data's, and the `ResizeObserver` is
   * left watching a detached node that will never fire again.
   *
   * Nothing errors. A frozen column simply sits at the wrong offset — overlapping
   * its neighbour by the difference, or floating away from the edge.
   *
   * Three of the first ten adopting pages hit this and each worked around it
   * differently: one unified its two mounts into one, one added a mount
   * discriminator to `frozenSideOf`'s identity. Both are correct and neither
   * should have been necessary, so the element identity is tracked HERE.
   */
  const observedRow = useRef<HTMLTableRowElement | null>(null);

  /*
   * 🔴 **THE OBSERVER LIVES IN A REF, NOT IN THE EFFECT'S CLEANUP — 2026-10-04.**
   * *SUPERSEDED: `return () => observer.disconnect();` from the effect below.*
   * That effect has no dependency array, so React ran its cleanup before EVERY
   * re-render — disconnecting the observer — while the body returned early on
   * every render that changed neither the row nor the signature, so nothing
   * reconnected it. The observer therefore died on the first unrelated render,
   * and any later width change (real data landing, fonts, a note wrapping) left
   * the offsets stale. Reported on /companies: a second left-frozen column was
   * still at offset 0 and sat exactly on top of Company. Nothing errored.
   *
   * `measureRef` gives the long-lived observer the CURRENT `measure`, whose
   * closure changes with the key list and the frozen sides.
   */
  const observer = useRef<ResizeObserver | null>(null);
  const measureRef = useRef(measure);

  useLayoutEffect(() => {
    measureRef.current = measure;
    const row = headRowRef.current;
    const rowChanged = observedRow.current !== row;
    const signatureChanged = lastSignature.current !== signature;
    // Runs on every render deliberately — the guard is the comparison, not a
    // dependency array, because the element identity is not a value React can
    // be told to watch. `setOffsets` already bails when nothing moved, so this
    // cannot loop. It returns NO cleanup: see `observer` above.
    if (!rowChanged && !signatureChanged) return;
    lastSignature.current = signature;
    observedRow.current = row;

    measure();
    observer.current?.disconnect();
    observer.current = null;
    if (row === null || typeof ResizeObserver === 'undefined') return;
    const next = new ResizeObserver(() => measureRef.current());
    next.observe(row);
    Array.from(row.children).forEach((cell) => next.observe(cell));
    observer.current = next;
  });

  // The one real teardown: when the table that owns this hook unmounts.
  useEffect(
    () => () => {
      observer.current?.disconnect();
      observer.current = null;
    },
    [],
  );

  return offsets;
}
